<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { api } from '@/api'
import { ChatterStatus } from '@/api/resources/chatters/model'
import { statuses } from '@/utils/status'
import { formatToHumanReadable } from '@/utils'
import { dateShortcuts } from '@/data/date-shortcuts'
import { ElMessage, ElMessageBox } from 'element-plus'
import type {
  BroadcastItem,
  BroadcastDetails,
  BroadcastFilters,
} from '@/api/custom/broadcast'
import dayjs from 'dayjs'

const router = useRouter()

const language = ref('en')
const selectedStatuses = ref<ChatterStatus[]>([])
const dateRange = ref<[Date | undefined, Date | undefined]>([undefined, undefined])
const noAssessment = ref(false)
const sendTest = ref(false)
const testNumber = ref(1)
const message = ref(
  `Good news — our referral program is open to everyone!\n\nKnow someone who'd make a great chatter? Invite them to join us. For every friend who signs up through your personal link and completes 5 trial shifts, you get $50 — and there's no limit on how many people you can invite.\n\nYour personal invite link:\n{referralLink}\n\nEveryone who joins through it is automatically linked to you. Want to see how your invites are doing? Just message me "show my referrals" anytime.`,
)
const chatterIdsInput = ref('')
const limit = ref<number | undefined>(undefined)
const campaignKey = ref('')

const previewCount = ref<number | null>(null)
const previewLoading = ref(false)
const sendLoading = ref(false)
const historyLoading = ref(false)
const broadcasts = ref<BroadcastItem[]>([])
const selectedBroadcast = ref<BroadcastDetails | null>(null)
const detailsVisible = ref(false)
const detailsLoading = ref(false)
const retryFailedLoading = ref(false)

const languages = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Russian' },
  { value: 'uk', label: 'Ukrainian' },
]

const statusOptions = Object.entries(statuses).map(([key, val]) => ({
  value: key,
  label: val.label,
}))

const parseChatterIds = (input: string): number[] =>
  input
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n > 0)

const buildFilters = () => {
  const ids = parseChatterIds(chatterIdsInput.value)
  return {
    language: language.value,
    statuses: selectedStatuses.value.length
      ? (selectedStatuses.value as string[])
      : undefined,
    statusChangedFrom: dateRange.value[0]
      ? dayjs(dateRange.value[0]).startOf('day').toISOString()
      : undefined,
    statusChangedTo: dateRange.value[1]
      ? dayjs(dateRange.value[1]).endOf('day').toISOString()
      : undefined,
    noAssessment: noAssessment.value || undefined,
    chatterIds: ids.length ? ids : undefined,
    limit: limit.value && limit.value > 0 ? limit.value : undefined,
    campaignKey: campaignKey.value.trim() || undefined,
  }
}

const insertReferralLink = () => {
  message.value = message.value
    ? `${message.value}\n{referralLink}`
    : '{referralLink}'
}

const generateCampaignKey = () => {
  const date = dayjs().format('YYYYMMDD')
  const rand = Math.random().toString(36).slice(2, 6)
  campaignKey.value = `${language.value}-${date}-${rand}`
}

const onPreview = async () => {
  previewLoading.value = true
  try {
    const result = await api.broadcast.preview({ payload: buildFilters() })
    previewCount.value = result.count
  } catch {
    previewCount.value = null
  } finally {
    previewLoading.value = false
  }
}

const onSend = async () => {
  if (!message.value.trim()) {
    ElMessage.warning('Enter a message')
    return
  }

  const warnNoKey = !!(
    limit.value &&
    limit.value > 0 &&
    !campaignKey.value.trim()
  )

  try {
    await ElMessageBox.confirm(
      warnNoKey
        ? `Send broadcast to ${previewCount.value} users?<br><br><b>Warning:</b> no campaign key — waves are not deduplicated, recipients may receive it more than once.`
        : `Send broadcast to ${previewCount.value} users?`,
      'Confirm',
      {
        confirmButtonText: 'Send',
        cancelButtonText: 'Cancel',
        type: 'warning',
        dangerouslyUseHTMLString: warnNoKey,
      },
    )
  } catch {
    return
  }

  sendLoading.value = true
  try {
    const result = await api.broadcast.send({
      payload: {
        ...buildFilters(),
        message: message.value,
        sendTest: sendTest.value || undefined,
        testNumber: sendTest.value ? testNumber.value : undefined,
      },
    })
    ElMessage.success(`Broadcast started: ${result.total} recipients`)
    await fetchHistory()
    // Refresh the remaining count so the next wave shows how many are left.
    onPreview()
  } catch {
    ElMessage.error('Failed to start broadcast')
  } finally {
    sendLoading.value = false
  }
}

const fetchHistory = async () => {
  historyLoading.value = true
  try {
    broadcasts.value = await api.broadcast.list()
  } catch {
    // handled by http-client interceptor
  } finally {
    historyLoading.value = false
  }
}

interface CampaignSummary {
  key: string
  wavesCount: number
  sent: number
  failed: number
  lastWaveAt: string
  status: string
  message: string
  filters: BroadcastFilters
}

const campaigns = computed<CampaignSummary[]>(() => {
  const map = new Map<string, BroadcastItem[]>()
  for (const b of broadcasts.value) {
    const key = b.campaign_key ?? b.filters?.campaignKey
    if (!key) continue
    const arr = map.get(key)
    if (arr) arr.push(b)
    else map.set(key, [b])
  }
  return [...map.entries()].map(([key, waves]) => {
    const sorted = [...waves].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    )
    const latest = sorted[0]
    return {
      key,
      wavesCount: waves.length,
      sent: waves.reduce((s, w) => s + w.sent, 0),
      failed: waves.reduce((s, w) => s + w.failed, 0),
      lastWaveAt: latest.created_at,
      status: latest.status,
      message: latest.message,
      filters: latest.filters,
    }
  })
})

const continueCampaign = (c: CampaignSummary) => {
  campaignKey.value = c.key
  message.value = c.message
  language.value = c.filters.language
  selectedStatuses.value = (c.filters.statuses ?? []) as ChatterStatus[]
  limit.value = c.filters.limit
  window.scrollTo({ top: 0, behavior: 'smooth' })
  onPreview()
}

const openDetails = async (row: BroadcastItem) => {
  detailsVisible.value = true
  detailsLoading.value = true
  try {
    selectedBroadcast.value = await api.broadcast.details({
      urlParams: { id: row.id },
    })
  } catch {
    selectedBroadcast.value = null
  } finally {
    detailsLoading.value = false
  }
}

const onRetryFailed = async () => {
  if (!selectedBroadcast.value) return

  const id = selectedBroadcast.value.id
  const failed = selectedBroadcast.value.failed

  try {
    await ElMessageBox.confirm(
      `Resend the broadcast to ${failed} failed recipients?`,
      'Confirm',
      {
        confirmButtonText: 'Resend',
        cancelButtonText: 'Cancel',
        type: 'warning',
      },
    )
  } catch {
    return
  }

  retryFailedLoading.value = true
  try {
    const result = await api.broadcast.retryFailed({ urlParams: { id } })
    ElMessage.success(`Retry started: ${result.total} recipients`)
    await fetchHistory()
    await openDetails({ id } as BroadcastItem)
  } finally {
    retryFailedLoading.value = false
  }
}

const getStatusType = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'success'
    case 'IN_PROGRESS':
      return 'warning'
    case 'FAILED':
      return 'danger'
    default:
      return 'info'
  }
}

const getRecipientStatusType = (status: string) => {
  switch (status) {
    case 'SENT':
      return 'success'
    case 'FAILED':
      return 'danger'
    default:
      return 'info'
  }
}

const truncate = (text: string, max = 60) =>
  text.length > max ? text.slice(0, max) + '...' : text

const formatFilters = (filters: BroadcastFilters) => {
  const parts: string[] = []
  if (filters.language) parts.push(filters.language.toUpperCase())
  parts.push(filters.statuses?.length ? `${filters.statuses.length} statuses` : 'All statuses')
  if (filters.chatterIds?.length) parts.push(`IDs: ${filters.chatterIds.length}`)
  if (filters.limit) parts.push(`Limit ${filters.limit}`)
  if (filters.campaignKey) parts.push(`Campaign: ${filters.campaignKey}`)
  return parts.join(', ')
}

fetchHistory()
</script>

<template>
  <main>
    <h2>Broadcast</h2>

    <el-card style="margin-bottom: 2em">
      <template #header>New Broadcast</template>

      <el-form label-position="top">
        <div style="display: flex; gap: 1em; flex-wrap: wrap">
          <el-form-item label="Language">
            <el-select v-model="language" style="width: 140px">
              <el-option
                v-for="lang in languages"
                :key="lang.value"
                :label="lang.label"
                :value="lang.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Statuses (empty = all)" style="flex: 1; min-width: 250px">
            <el-select
              v-model="selectedStatuses"
              multiple
              collapse-tags
              collapse-tags-tooltip
              placeholder="All statuses"
              style="width: 100%"
            >
              <el-option
                v-for="s in statusOptions"
                :key="s.value"
                :label="s.label"
                :value="s.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Status changed date">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              start-placeholder="From"
              end-placeholder="To"
              :shortcuts="dateShortcuts"
              style="width: 300px"
            />
          </el-form-item>
        </div>

        <div style="display: flex; gap: 1em; flex-wrap: wrap">
          <el-form-item label="Campaign key (for dedup across waves)" style="flex: 1; min-width: 240px">
            <div style="display: flex; gap: 0.5em; width: 100%">
              <el-input
                v-model="campaignKey"
                placeholder="e.g. ru-launch-2026-05"
              />
              <el-button @click="generateCampaignKey">Generate</el-button>
            </div>
          </el-form-item>

          <el-form-item label="Limit (max recipients per wave)">
            <el-input-number
              v-model="limit"
              :min="1"
              :controls="false"
              placeholder="∞"
              style="width: 140px"
            />
          </el-form-item>
        </div>

        <el-form-item label="Chatter IDs (smoke-test; comma or newline separated, overrides bulk filters except language)">
          <el-input
            v-model="chatterIdsInput"
            type="textarea"
            :rows="2"
            placeholder="123, 456"
          />
        </el-form-item>

        <div style="display: flex; gap: 1em; align-items: center; margin-bottom: 1em">
          <el-switch v-model="noAssessment" active-text="No assessment" />
          <el-switch v-model="sendTest" active-text="Send test" />
          <el-input-number
            v-if="sendTest"
            v-model="testNumber"
            :min="1"
            :max="10"
            size="small"
          />
        </div>

        <el-form-item label="Message">
          <el-input
            v-model="message"
            type="textarea"
            :rows="5"
            placeholder="Message text"
          />
          <div style="margin-top: 0.5em; display: flex; gap: 0.75em; align-items: center; flex-wrap: wrap">
            <el-button size="small" @click="insertReferralLink">
              Insert {referralLink}
            </el-button>
            <span style="color: #999; font-size: 0.85em">
              <code>{referralLink}</code> is replaced with each recipient's personal referral link.
            </span>
          </div>
        </el-form-item>

        <div style="display: flex; gap: 1em; align-items: center">
          <el-button
            type="info"
            :loading="previewLoading"
            @click="onPreview"
          >
            Preview
          </el-button>

          <el-tag v-if="previewCount !== null" size="large" type="info">
            {{ previewCount }} recipients
          </el-tag>

          <el-button
            type="primary"
            :loading="sendLoading"
            :disabled="previewCount === null || previewCount === 0"
            @click="onSend"
          >
            Send
          </el-button>
        </div>
      </el-form>
    </el-card>

    <el-card v-if="campaigns.length" style="margin-bottom: 2em">
      <template #header>Campaigns (waves)</template>

      <el-table :data="campaigns" style="width: 100%">
        <el-table-column label="Campaign key" prop="key" min-width="180" />
        <el-table-column label="Waves" prop="wavesCount" width="80" />
        <el-table-column label="Sent" prop="sent" width="80" />
        <el-table-column label="Failed" prop="failed" width="80" />

        <el-table-column label="Last wave" width="160">
          <template #default="{ row }">
            {{ formatToHumanReadable(row.lastWaveAt) }}
          </template>
        </el-table-column>

        <el-table-column label="Status" width="120">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Action" width="160">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="continueCampaign(row)">
              Send next wave
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card>
      <template #header>
        <div style="display: flex; justify-content: space-between; align-items: center">
          <span>History</span>
          <el-button text :loading="historyLoading" @click="fetchHistory">
            Refresh
          </el-button>
        </div>
      </template>

      <el-table
        :data="broadcasts"
        style="width: 100%"
        @row-click="openDetails"
        :row-style="{ cursor: 'pointer' }"
      >
        <el-table-column label="Date" width="160">
          <template #default="{ row }">
            {{ formatToHumanReadable(row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column label="Message">
          <template #default="{ row }">
            {{ truncate(row.message) }}
          </template>
        </el-table-column>

        <el-table-column label="Filters" width="160">
          <template #default="{ row }">
            {{ formatFilters(row.filters) }}
          </template>
        </el-table-column>

        <el-table-column label="Total" width="80" prop="total" />
        <el-table-column label="Sent" width="80" prop="sent" />
        <el-table-column label="Failed" width="80" prop="failed" />

        <el-table-column label="Status" width="120">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog v-model="detailsVisible" title="Broadcast Details" width="700px">
      <div v-if="detailsLoading" v-loading="true" style="min-height: 200px" />
      <template v-else-if="selectedBroadcast">
        <el-descriptions :column="2" border style="margin-bottom: 1em">
          <el-descriptions-item label="Status">
            <el-tag :type="getStatusType(selectedBroadcast.status)" size="small">
              {{ selectedBroadcast.status }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="Created">
            {{ formatToHumanReadable(selectedBroadcast.created_at) }}
          </el-descriptions-item>
          <el-descriptions-item label="Total">{{ selectedBroadcast.total }}</el-descriptions-item>
          <el-descriptions-item label="Sent">{{ selectedBroadcast.sent }}</el-descriptions-item>
          <el-descriptions-item label="Failed">{{ selectedBroadcast.failed }}</el-descriptions-item>
          <el-descriptions-item label="Message" :span="2">
            <pre style="white-space: pre-wrap; margin: 0">{{ selectedBroadcast.message }}</pre>
          </el-descriptions-item>
        </el-descriptions>

        <el-table :data="selectedBroadcast.recipients" style="width: 100%" max-height="400">
          <el-table-column label="Chatter" width="200">
            <template #default="{ row }">
              <el-link
                v-if="row.chatter"
                type="primary"
                :underline="false"
                @click="router.push({ query: { chatter: row.chatter.id } })"
              >
                {{ row.chatter.name || 'N/A' }}
              </el-link>
              <span v-else>N/A</span>
              <span v-if="row.chatter?.tg_peers?.[0]?.username" style="color: #999">
                @{{ row.chatter.tg_peers[0].username }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Status" width="100">
            <template #default="{ row }">
              <el-tag :type="getRecipientStatusType(row.status)" size="small">
                {{ row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Sent at" width="160">
            <template #default="{ row }">
              {{ row.sent_at ? formatToHumanReadable(row.sent_at) : '-' }}
            </template>
          </el-table-column>
          <el-table-column label="Error">
            <template #default="{ row }">
              <span v-if="row.error" style="color: #f56c6c">{{ row.error }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
      </template>
      <template #footer>
        <el-button @click="detailsVisible = false">Close</el-button>
        <el-button
          v-if="selectedBroadcast && selectedBroadcast.failed > 0"
          type="warning"
          :loading="retryFailedLoading"
          @click="onRetryFailed"
        >
          Retry {{ selectedBroadcast.failed }} failed
        </el-button>
      </template>
    </el-dialog>
  </main>
</template>
